/**
 * Public Real Estate search — map viewport + Buy/Rent + free-form query.
 */

export const SHOW_REAL_ESTATE_ADVANCED_FILTERS = false;

export const REAL_ESTATE_LISTING_MODES = {
  RENT: "rent",
  SALE: "sale",
};

export const REAL_ESTATE_SEARCH_DEBOUNCE_MS = 400;

export const INITIAL_REAL_ESTATE_SEARCH = {
  listingMode: REAL_ESTATE_LISTING_MODES.RENT,
  query: "",
  bbox: null,
};

export function listingModeToTransactionType(listingMode) {
  return listingMode === REAL_ESTATE_LISTING_MODES.SALE
    ? REAL_ESTATE_LISTING_MODES.SALE
    : REAL_ESTATE_LISTING_MODES.RENT;
}

export function transactionTypeToListingMode(transactionType) {
  return transactionType === REAL_ESTATE_LISTING_MODES.SALE
    ? REAL_ESTATE_LISTING_MODES.SALE
    : REAL_ESTATE_LISTING_MODES.RENT;
}

export function bboxCenter(bounds) {
  if (!bounds) {
    return null;
  }

  const { west, south, east, north } = bounds;
  if (
    ![west, south, east, north].every((value) => Number.isFinite(Number(value)))
  ) {
    return null;
  }

  return {
    lng: (Number(west) + Number(east)) / 2,
    lat: (Number(south) + Number(north)) / 2,
  };
}

/**
 * User-facing status line for list/split views.
 * Area total stays authoritative; when bubbles are active, cards are a sample.
 */
export function formatRealEstateResultsMessage({
  total = 0,
  shown = 0,
  query = "",
  listingMode = REAL_ESTATE_LISTING_MODES.RENT,
  limitReached = false,
  limit = 250,
  clientViewName = "",
  areaBubblesActive = false,
  focusCellCount = null,
} = {}) {
  const isSale = listingMode === REAL_ESTATE_LISTING_MODES.SALE;
  const kindLabel = isSale ? "homes for sale" : "rentals";
  const trimmedQuery = String(query || "").trim();
  const trimmedClientView = String(clientViewName || "").trim();
  const focusedCount = Number(focusCellCount);
  const hasFocusCell = Number.isFinite(focusedCount) && focusedCount > 0;

  if (total === 0 && shown === 0) {
    if (trimmedClientView) {
      return {
        primary: `No properties found for this Client View.`,
        secondary: `${trimmedClientView} has no matching properties in this map area. Try zooming out or clearing filters.`,
      };
    }

    return {
      primary: "No properties found in this map area.",
      secondary: "Try zooming out or clearing your search.",
    };
  }

  const count = total > 0 ? total : shown;
  let primary;

  if (hasFocusCell) {
    primary = trimmedQuery
      ? `Showing ${count} ${kindLabel} matching “${trimmedQuery}” in this neighborhood`
      : `Showing ${count} ${kindLabel} in this neighborhood`;
  } else if (trimmedQuery) {
    primary = `Showing ${count} ${kindLabel} matching “${trimmedQuery}” in this map area`;
  } else {
    primary = `Showing ${count} ${kindLabel} in this map area`;
  }

  if (trimmedClientView) {
    primary = `${primary} (${trimmedClientView})`;
  }

  let secondary = null;
  if (hasFocusCell) {
    secondary = "Focused from a map bubble. Zoom out to see the full area.";
  } else if (limitReached && areaBubblesActive) {
    secondary = `Map bubbles show full area counts. List shows the first ${limit} — zoom in or click a bubble.`;
  } else if (limitReached) {
    secondary = `Showing first ${limit} results. Zoom in or refine your search.`;
  } else if (areaBubblesActive) {
    secondary = "Bubble counts cover the full area; edge bubbles may be clipped by the map view.";
  } else if (trimmedClientView) {
    secondary = "Client View properties only";
  }

  return { primary, secondary };
}
